import moment from 'moment';

/**
 * Platform-wide default buffer (in days) subtracted from a USDA animal's
 * cut-date-derived reserve deadline. Changing the buffer must stay a one-line
 * edit of this constant — do not hardcode it anywhere else.
 */
export const DEFAULT_USDA_RESERVE_BUFFER_DAYS = 0;

/**
 * Minimal structural shape required by {@link reserveDeadlineFor}. Kept
 * dependency-free (no `packages/api` import) so both `api` and `web` derive
 * the same reserve deadline from denormalized/lean data. `inspectionLevel` is
 * a plain string (not the api-only `EInspectionLevel` enum), compared
 * case-insensitively against `'usda'` per `inspectionLevelHelper`'s convention.
 */
export interface ReserveDeadlineAnimal {
  inspectionLevel?: string | null;
  killDate?: Date | null;
  cutDate?: Date | null;
}

export interface ReserveDeadlineOptions {
  /**
   * Days to pull the USDA deadline earlier than its base date
   * (`cutDate ?? killDate`). Defaults to {@link DEFAULT_USDA_RESERVE_BUFFER_DAYS};
   * never applied to non-USDA inspection levels. An option (not hardcoded) so
   * a per-processor setting can be passed in later without reshaping call sites.
   */
  usdaBufferDays?: number;
}

/**
 * Derives an animal's buyer-marketplace reserve deadline — the single source
 * of truth for when claims on the animal close:
 *
 *  - `usda`: `cutDate ?? killDate` minus `opts.usdaBufferDays` — reservable
 *    through the whole hang period, closing at (or before) the cut.
 *  - `state`/`exempt`/unknown: `killDate`; the buffer never applies.
 *  - Returns `null` when the selected date is null — no date-based deadline
 *    (sellable until a status-based gate closes it).
 *
 * Pure (no clock read, no DB) — callers compare the returned Date against
 * `now` themselves. The deadline closes at the start of the deadline day:
 * gate with `now >= deadline → closed`.
 * @param animal - Animal dates + inspection level to derive from.
 * @param opts - Optional overrides (e.g. USDA buffer days).
 * @returns The exclusive close instant, or null when no date-based deadline.
 */
export function reserveDeadlineFor(
  animal: ReserveDeadlineAnimal,
  opts?: ReserveDeadlineOptions,
): Date | null {
  const isUsda = animal.inspectionLevel?.toLowerCase() === 'usda';

  if (!isUsda) {
    return animal.killDate ?? null;
  }

  const baseDate = animal.cutDate ?? animal.killDate ?? null;
  if (baseDate == null) return null;

  const bufferDays = opts?.usdaBufferDays ?? DEFAULT_USDA_RESERVE_BUFFER_DAYS;
  if (!bufferDays) return baseDate;

  return moment.utc(baseDate).subtract(bufferDays, 'days').toDate();
}

/**
 * Derives the buyer-facing "Reserve by" date — the last calendar day on which a
 * buyer can still reserve the animal.
 *
 * {@link reserveDeadlineFor} returns the *exclusive close instant*: the enforcement
 * gate closes as soon as `now >= deadline`, and that instant sits at the start of
 * the deadline day (`cutDate` for USDA, `killDate` for state/exempt). A buyer can
 * therefore reserve right up until — but not on — that day, so the last day they
 * can actually act is the day before. Display code must show *this* date, not the
 * raw enforcement instant, or the card would advertise a day the gate rejects.
 *
 * Returns `null` when there is no date-based deadline. Pure (no clock read, no DB);
 * enforcement paths keep calling {@link reserveDeadlineFor} directly — do not swap
 * those for this display helper.
 * @param animal - Animal dates + inspection level to derive from.
 * @param opts - Optional overrides (e.g. USDA buffer days).
 * @returns The last reservable calendar day, or null when no deadline.
 */
export function reserveByDisplayDate(
  animal: ReserveDeadlineAnimal,
  opts?: ReserveDeadlineOptions,
): Date | null {
  const deadline = reserveDeadlineFor(animal, opts);
  if (deadline == null) return null;

  return moment.utc(deadline).subtract(1, 'day').toDate();
}
